<script setup>
import config from '@/common/config';
import { addIfr, ensureMeta, get, kakaoLayer, loadEpsgsWithPrjInit, naverLayer, qsel } from '@/common/util';
import { useMetaStore } from '@/stores';
import TileLayer from 'ol/layer/Tile';
import { OSM, XYZ } from 'ol/source';
import { onMounted, onUnmounted, reactive } from 'vue';

const props = defineProps({
  map: Object,
  initid: String,
  hidden: Boolean,
  width: String,
  onClose: {
    type: Function,
    required: false
  }, onUpdate: {
    type: Function,
    required: false
  }, onLoad: {
    type: Function,
    required: false
  }
})
const metaStore = useMetaStore()
const meta = reactive({
  selbgmapid: props.initid || '',
  bgmaps: props.bgmaps || [],
  map: props.user?.mapinstance,
})
function addLayer(bm) {
  let layer, bmtype = bm.bmtype.toLowerCase()
  if (bmtype == 'osm') {
    layer = new TileLayer({ source: new OSM() })
  } else if (bmtype.includes('vworld')) {
    let ext = 'png', maptype
    if (bmtype.includes('base')) maptype = 'Base'
    if (bmtype.includes('white')) maptype = 'white'
    if (bmtype.includes('midnight')) maptype = 'midnight'
    if (bmtype.includes('hybrid')) maptype = 'Hybrid'
    if (bmtype.includes('sate')) {
      maptype = 'Satellite'
      ext = 'jpeg'
    }
    let url = `https://api.vworld.kr/req/wmts/1.0.0/${meta.vwapikey}/${maptype}/{z}/{y}/{x}.${ext}`
    layer = new TileLayer({ source: new XYZ({ url: url }), extent: config.KoreaBbox3857 })
  } else if (bmtype.includes('google')) {
    let maptype
    if (bmtype.includes('satellite')) maptype = 'y'
    if (bmtype.includes('base')) maptype = 'm'
    let url = `http://mt{0-3}.google.com/vt/lyrs=${maptype}&hl=ko&x={x}&y={y}&z={z}`
    layer = new TileLayer({ source: new XYZ({ url: url }) })
  } else if (bmtype == 'naver') {
    addIfr('nmifr', config.backend + 'extmap/naver/' + meta.navermapidapi)
  } else if (bmtype == 'kakao') {
    addIfr('kmifr', config.backend + 'extmap/kakao/' + meta.kakaomapkey)
  } else if (bmtype.includes('directinput')) {
    layer = new TileLayer({ source: new XYZ({ url: bm.url }) })
  }
  if (layer) {
    layer.set('bmtype', bm.bmtype)
    layer.set('id', bm.id)
    layer.setVisible(false)
    meta.basemapgroup.getLayers().push(layer)
    if (bm.initial) {
      meta.selbgmapid = bm.id
      selBgMap(bm.id)
    }
  }
}
function initExtBaseMap(bnm, basesrc, ext, query) {
  let layer
  if (bnm == 'NAVER') layer = naverLayer(basesrc, ext, query)
  else if (bnm == 'KAKAO') layer = kakaoLayer(meta.map, basesrc, ext)
  const bm = meta.bgmaps.find(bgm => bgm.nm.toLowerCase().includes(bnm.toLowerCase()))
  layer.set('bmtype', bm.bmtype)
  layer.set('id', bm.id)
  layer.setVisible(bm.initial)
  meta.basemapgroup.getLayers().push(layer)
  if (bm.initial) {
    meta.selbgmapid = bm.id
    selBgMap(bm.id)
  }
}
function hideAll() {
  meta.basemapgroup.getLayersArray().forEach(lyr => lyr.setVisible(false))
}
function selBgMap(id) {
  hideAll()
  const sellayer = meta.basemapgroup.getLayersArray().find(lyr => lyr.get('id') == id)
  sellayer.setVisible(true)
  const bmtype = sellayer.get('bmtype')
  const hasvworkakaoornaver = meta.bgmaps.some(bm => bm.bmtype.toLowerCase().includes('kakao') || bm.bmtype.toLowerCase().includes('vworld') || bm.bmtype.toLowerCase().includes('naver'))
  if (hasvworkakaoornaver && bmtype != 'OSM') {
    const vworkakaonaverselected = bmtype.toLowerCase().includes('kakao') || bmtype.toLowerCase().includes('vworld') || bmtype.toLowerCase().includes('naver')
    meta.basemapgroup.getLayersArray().find(lyr => lyr.get('bmtype') == 'OSM').setVisible(vworkakaonaverselected)
  }
  if (meta.map.getView().getZoom() > 19) meta.map.getView().setZoom(19)
  if (props.onUpdate) props.onUpdate(meta.selbgmapid)
}
async function init() {
  let res = await get('rest/authed/member/confswkeyin/initmapzoom,initmapcenpos,vwdomain,vwapikey,navermapidapi,kakaomapkey')
  meta.vwdomain = res.data.find(conf => conf.wkey == 'vwdomain')?.sval || null
  meta.vwapikey = res.data.find(conf => conf.wkey == 'vwapikey')?.sval || null
  meta.navermapidapi = res.data.find(conf => conf.wkey == 'navermapidapi')?.sval || null
  meta.kakaomapkey = res.data.find(conf => conf.wkey == 'kakaomapkey')?.sval || null
  if (!meta.vwapikey) metaStore.toast('브이월드 키가 없습니다.')
  res = await get('rest/authed/member/basemaps')
  if (res.data && res.data.length) {
    meta.bgmaps = res.data.filter(bg => bg.enabled)
    meta.selbgmapid = meta.bgmaps.find(bg => bg.initial)
    const oidx = meta.bgmaps.findIndex(item => item.bmtype == 'OSM')
    if (oidx > -1) {
      const [target] = meta.bgmaps.splice(oidx, 1)
      meta.bgmaps.unshift(target)
    }
  } else {
    meta.bgmaps = [{ id: 'osm', bmtype: 'OSM', nm: 'OSM', initial: true }]
  }
  if (meta.bgmaps.findIndex(item => item.bmtype == 'OSM') < 0) meta.bgmaps.unshift({ id: 'osm', bmtype: 'OSM', nm: 'OSM' })
  if (props.initid) meta.bgmaps.forEach(bg => bg.initial = bg.id == props.initid)
  let initbg = meta.bgmaps.find(bg => bg.initial)
  if (!initbg) {
    initbg = meta.bgmaps.find(bg => bg.bmtype == 'OSM')
    initbg.initial = true
    meta.selbgmapid = initbg.id
  }
  if (!meta.selbgmapid) meta.selbgmapid = meta.bgmaps[0].id
  meta.map = props.map
  meta.basemapgroup = meta.map.getLayers().getArray().find(lyr => lyr.get('nm') == 'basemapgroup')
  meta.bgmaps.forEach(addLayer)
  if (props.onLoad) props.onLoad(meta.bgmaps, meta.selbgmapid)
}
const winMsgHandler = evt => {
  if (evt.data.type === "initExtBaseMap") initExtBaseMap(evt.data.nm, evt.data.basesrc, evt.data.ext, evt.data.query)
}
defineExpose({ hideAll, selBgMap })
onMounted(() => {
  window.addEventListener("message", winMsgHandler)
  loadEpsgsWithPrjInit(() => ensureMeta(props, 'map', init))
})
onUnmounted(() => {
  window.removeEventListener("message", winMsgHandler)
})
</script>
<template>
  <v-card :class="`bgcard${props.hidden ? ' d-none' : ''}`" min-width="280" :width="props.width">
    <v-card-title>
      <v-row v-if="props.onClose">
        <v-col>{{ $t('backgroundmap') }}</v-col>
        <v-col cols="3" class="text-right"><v-btn variant="flat" density="compact" icon="mdi-close"
            @click="props.onClose && props.onClose()" /></v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-select v-if="meta.selbgmapid" hide-details variant="outlined" density="compact"
        :label="$t('pleaseselectabackgroundmap')" :items="meta.bgmaps" v-model="meta.selbgmapid" item-title="nm"
        item-value="id" @update:model-value="selBgMap" />
    </v-card-text>
  </v-card>
</template>
